import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, onSnapshot, increment, getDoc, setDoc,
  getDocs, query, where, arrayUnion, orderBy, limit
} from 'firebase/firestore';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Plus, Megaphone, BookOpen, Users, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, Eye, Send, Bell, Rocket, ArrowRight, ArrowLeft,
  MessageCircle, Star, UserMinus
} from 'lucide-react';

// Campaign sub-statuses for the ARC lifecycle
const CAMPAIGN_STAGES = [
  { id: 'note_interest', label: 'Note Interest',  icon: Eye,   colour: 'bg-blue-100 text-blue-700',   desc: 'Pre sign-up — readers can register interest' },
  { id: 'active',        label: 'Active',          icon: Send,  colour: 'bg-green-100 text-green-700',  desc: 'ARCs are open — readers can apply' },
  { id: 'closed',        label: 'Closed',          icon: Bell,  colour: 'bg-orange-100 text-orange-700', desc: 'Closed — waitlist & pre-order notifications' },
  { id: 'live',          label: 'Live',             icon: Rocket, colour: 'bg-teal-100 text-teal-700',   desc: 'Book is launched and live' },
];

// Legacy status mapping
function normaliseStatus(status) {
  if (status === 'open') return 'active';
  if (status === 'draft') return 'note_interest';
  return status;
}

function getStageIndex(status) {
  const normalised = normaliseStatus(status);
  const idx = CAMPAIGN_STAGES.findIndex(s => s.id === normalised);
  return idx >= 0 ? idx : 0;
}

function getStageConfig(status) {
  const normalised = normaliseStatus(status);
  return CAMPAIGN_STAGES.find(s => s.id === normalised) || CAMPAIGN_STAGES[0];
}

export default function AuthorCampaignsTab() {
  const { authorId } = useAuth();
  const { campaigns, books, basePath } = useAuthorDashboard();
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [applications, setApplications] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [bookId, setBookId] = useState('');
  const [spotsAvailable, setSpotsAvailable] = useState(20);
  const [openDate, setOpenDate] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [requirements, setRequirements] = useState('');
  const [preferredProfiles, setPreferredProfiles] = useState('');
  const [arcFormat, setArcFormat] = useState('ebook');
  const [pitch, setPitch] = useState('');

  // Compose message modal state
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [composeRecipient, setComposeRecipient] = useState(null); // { readerId, readerName, readerEmail }
  const [composeBookTitle, setComposeBookTitle] = useState('');
  const [composeBookId, setComposeBookId] = useState('');
  const [composeText, setComposeText] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Remove from ARC modal state
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // { app, campaignId, campaign }
  const [isRemoving, setIsRemoving] = useState(false);

  // Stage-back confirmation modal state
  const [showStageBackModal, setShowStageBackModal] = useState(false);
  const [stageBackTarget, setStageBackTarget] = useState(null); // { campaign, previousStage }

  // Email send log for expanded campaign
  const [campaignEmails, setCampaignEmails] = useState([]);

  // Load applications for expanded campaign
  useEffect(() => {
    if (!expandedCampaign || !basePath) { setApplications([]); return; }
    const unsub = onSnapshot(
      collection(db, basePath, 'campaigns', expandedCampaign, 'applications'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setApplications(items);
      }
    );
    return unsub;
  }, [expandedCampaign, basePath]);

  // Load email sends for expanded campaign's book
  useEffect(() => {
    if (!expandedCampaign || !authorId) { setCampaignEmails([]); return; }
    const campaign = campaigns.find(c => c.id === expandedCampaign);
    if (!campaign?.bookId) { setCampaignEmails([]); return; }
    const q = query(
      collection(db, 'emailRequests'),
      where('authorId', '==', authorId),
      where('bookId', '==', campaign.bookId),
      orderBy('sentAt', 'desc'),
      limit(50)
    );
    getDocs(q).then(snap => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setCampaignEmails(list);
    }).catch(() => setCampaignEmails([]));
  }, [expandedCampaign, authorId, campaigns]);

  // One-time fix: initialise spotsRemaining on campaigns that don't have it yet
  useEffect(() => {
    if (!campaigns.length || !basePath) return;
    campaigns.forEach(async (campaign) => {
      if (campaign.spotsRemaining != null) return; // already set
      try {
        const appsSnap = await getDocs(
          query(
            collection(db, basePath, 'campaigns', campaign.id, 'applications'),
            where('status', '==', 'approved')
          )
        );
        const approvedCount = appsSnap.size;
        const spotsRemaining = Math.max(0, (campaign.spotsAvailable || 0) - approvedCount);
        console.log(`[ARC] Initialising spotsRemaining for "${campaign.name}": ${spotsRemaining} (${campaign.spotsAvailable} - ${approvedCount} approved)`);
        await updateDoc(doc(db, basePath, 'campaigns', campaign.id), {
          spotsRemaining,
          approvedCount,
        });
      } catch (err) {
        console.error(`[ARC] Failed to initialise spotsRemaining for ${campaign.id}:`, err);
      }
    });
  }, [campaigns, basePath]);

  function resetForm() {
    setName(''); setBookId(''); setSpotsAvailable(20); setOpenDate('');
    setCloseDate(''); setRequirements(''); setPreferredProfiles('');
    setArcFormat('ebook'); setPitch(''); setEditingCampaign(null); setError('');
  }

  function openEdit(campaign) {
    setName(campaign.name || '');
    setBookId(campaign.bookId || '');
    setSpotsAvailable(campaign.spotsAvailable || 20);
    setOpenDate(campaign.openDate || '');
    setCloseDate(campaign.closeDate || '');
    setRequirements(campaign.requirements || '');
    setPreferredProfiles(campaign.preferredProfiles || '');
    setArcFormat(campaign.arcFormat || 'ebook');
    setPitch(campaign.pitch || '');
    setEditingCampaign(campaign);
    setShowForm(true);
  }

  async function handleSave() {
    if (!bookId) { setError('Please select a book'); return; }
    setIsSubmitting(true);
    setError('');
    try {
      const selectedBook = books.find(b => b.id === bookId);
      const data = {
        name: name.trim() || selectedBook?.title || 'Untitled Campaign',
        bookId,
        bookTitle: selectedBook?.title || '',
        bookCoverUrl: selectedBook?.coverUrl || '',
        spotsAvailable: Number(spotsAvailable),
        openDate, closeDate,
        requirements: requirements.trim(),
        preferredProfiles: preferredProfiles.trim(),
        arcFormat,
        pitch: pitch.trim(),
        genres: selectedBook?.genres || [],
        authorId,
        authorName: selectedBook?.authorName || '',
        updatedAt: serverTimestamp(),
      };

      if (editingCampaign) {
        // If spots changed, recalculate spotsRemaining
        const oldSpots = editingCampaign.spotsAvailable || 0;
        const newSpots = Number(spotsAvailable);
        if (newSpots !== oldSpots) {
          const approved = editingCampaign.approvedCount || 0;
          const newRemaining = newSpots - approved;
          data.spotsRemaining = Math.max(0, newRemaining);
          // If spots opened up and campaign was closed, revert to active
          if (newRemaining > 0 && normaliseStatus(editingCampaign.status) === 'closed') {
            data.status = 'active';
          }
        }
        await updateDoc(doc(db, basePath, 'campaigns', editingCampaign.id), data);
      } else {
        data.status = 'note_interest';
        data.createdAt = serverTimestamp();
        data.pendingCount = 0;
        data.approvedCount = 0;
        data.spotsRemaining = Number(spotsAvailable);
        await addDoc(collection(db, basePath, 'campaigns'), data);
      }

      resetForm();
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCampaignStageChange(campaign, newStatus) {
    const updates = {
      status: newStatus,
      updatedAt: serverTimestamp(),
    };
    await updateDoc(doc(db, basePath, 'campaigns', campaign.id), updates);

    // If setting to "live", auto-set the book to "launched"
    if (newStatus === 'live' && campaign.bookId) {
      await updateDoc(doc(db, basePath, 'books', campaign.bookId), {
        status: 'launched',
        arcAvailable: true,
        updatedAt: serverTimestamp(),
      });
    }
  }

  function openStageBackConfirm(campaign) {
    const currentIndex = getStageIndex(campaign.status);
    if (currentIndex <= 0) return;
    const previousStage = CAMPAIGN_STAGES[currentIndex - 1];
    setStageBackTarget({ campaign, previousStage });
    setShowStageBackModal(true);
  }

  async function handleCampaignStageBack() {
    if (!stageBackTarget || !basePath) return;
    const { campaign, previousStage } = stageBackTarget;
    const newStatus = previousStage.id;

    const updates = {
      status: newStatus,
      updatedAt: serverTimestamp(),
    };

    // If going back from live → closed, revert book to pre_launch_active
    if (normaliseStatus(campaign.status) === 'live' && campaign.bookId) {
      await updateDoc(doc(db, basePath, 'books', campaign.bookId), {
        status: 'pre_launch_active',
        arcAvailable: true,
        updatedAt: serverTimestamp(),
      });
    }

    await updateDoc(doc(db, basePath, 'campaigns', campaign.id), updates);

    setShowStageBackModal(false);
    setStageBackTarget(null);
  }

  async function handleApplicationDecision(campaignId, applicationId, decision) {
    // Get current application status before updating
    const appRef = doc(db, basePath, 'campaigns', campaignId, 'applications', applicationId);
    const appSnap = await getDoc(appRef);
    const previousStatus = appSnap.exists() ? appSnap.data().status : null;

    await updateDoc(appRef, { status: decision, decidedAt: serverTimestamp() });

    const campaignRef = doc(db, basePath, 'campaigns', campaignId);

    // Look up the campaign's bookId and the applicant's readerId/email
    const campaign = campaigns.find(c => c.id === campaignId);
    const campaignBookId = campaign?.bookId;
    const appData = appSnap.exists() ? appSnap.data() : {};
    const readerId = appData.readerId;
    const readerEmail = appData.readerEmail;

    // Helper: look up global reader profile for cloning into per-book record
    async function getGlobalReaderProfile() {
      for (const id of [readerId, readerEmail].filter(Boolean)) {
        try {
          const snap = await getDoc(doc(db, 'readers', id));
          if (snap.exists()) return { id: snap.id, ...snap.data() };
        } catch { /* try next */ }
      }
      return null;
    }

    if (decision === 'approved' && previousStatus !== 'approved') {
      // Decrement spotsRemaining and increment approvedCount
      console.log(`[ARC] Approving ${applicationId} — decrementing spotsRemaining on campaign ${campaignId}`);
      await updateDoc(campaignRef, {
        spotsRemaining: increment(-1),
        approvedCount: increment(1),
      });

      // Create independent per-book reader record
      if (campaignBookId) {
        const docId = readerId || readerEmail;
        console.log(`[ARC] Creating per-book reader ${docId} for book ${campaignBookId}`);
        const profile = await getGlobalReaderProfile();
        await setDoc(doc(db, basePath, 'books', campaignBookId, 'readers', docId), {
          name: profile?.name || appData.readerName || '',
          emailCorrespondence: profile?.emailCorrespondence || readerEmail || '',
          eReaderEmail: profile?.eReaderEmail || '',
          source: profile?.source || 'reader_portal',
          acceptsMarketing: profile?.acceptsMarketing ?? true,
          status: profile?.status || 'Accepted',
          hasApprovedArcRequest: true,
          arcSent: '', reviewDate: '', rating: '', reviewRating: '',
          totalPoints: 0, totalReviews: 0,
          signupDate: profile?.signupDate || new Date().toISOString().split('T')[0],
          notes: '',
          interestedIn: profile?.interestedIn || 'eBook ARC request',
          reviewPlatforms: profile?.reviewPlatforms || [],
          socialUsernames: profile?.socialUsernames || '',
          primaryPlatform: profile?.primaryPlatform || 'Instagram',
          followerCount: profile?.followerCount || 0,
          location: profile?.location || '',
          favoriteBooks: profile?.favoriteBooks || '',
          bookId: campaignBookId,
          masterReaderId: docId,
          applicationStatus: 'approved',
          createdAt: serverTimestamp(),
          lastModified: serverTimestamp(),
        }, { merge: true });
      }

      // Sync booksReceived on the global reader doc so ReaderPortal "My Books" works.
      // emailCorrespondence is always stored lowercase on the global reader doc;
      // lowercase the lookup key here so we don't create an orphan duplicate
      // when the application's readerEmail has different casing.
      if (campaignBookId && readerEmail) {
        const normalisedEmail = readerEmail.toLowerCase();
        const globalQ = query(collection(db, 'readers'), where('emailCorrespondence', '==', normalisedEmail));
        const globalSnap = await getDocs(globalQ);
        if (!globalSnap.empty) {
          await updateDoc(globalSnap.docs[0].ref, {
            booksReceived: arrayUnion(campaignBookId),
            hasApprovedArcRequest: true,
            lastModified: serverTimestamp(),
          });
        } else {
          await addDoc(collection(db, 'readers'), {
            name: appData.readerName || '',
            emailCorrespondence: normalisedEmail,
            booksReceived: [campaignBookId],
            hasApprovedArcRequest: true,
            source: 'reader_portal',
            signupDate: new Date().toISOString().split('T')[0],
            createdAt: serverTimestamp(),
            status: 'Noted ARC interest',
          });
        }
      }

      // Check if spots have run out — auto-close campaign
      const campaignSnap = await getDoc(campaignRef);
      if (campaignSnap.exists()) {
        const data = campaignSnap.data();
        console.log(`[ARC] After approval — spotsRemaining: ${data.spotsRemaining}, status: ${data.status}`);
        if ((data.spotsRemaining ?? 0) <= 0 && normaliseStatus(data.status) === 'active') {
          console.log(`[ARC] No spots remaining — auto-closing campaign ${campaignId}`);
          await updateDoc(campaignRef, { status: 'closed', updatedAt: serverTimestamp() });
        }
      }
    } else if (previousStatus === 'approved' && decision !== 'approved') {
      // Undo: give the spot back
      console.log(`[ARC] Revoking approval for ${applicationId} — incrementing spotsRemaining on campaign ${campaignId}`);
      await updateDoc(campaignRef, {
        spotsRemaining: increment(1),
        approvedCount: increment(-1),
      });

      // Delete per-book reader record
      if (campaignBookId) {
        const docId = readerId || readerEmail;
        console.log(`[ARC] Deleting per-book reader ${docId} from book ${campaignBookId}`);
        try {
          await deleteDoc(doc(db, basePath, 'books', campaignBookId, 'readers', docId));
        } catch { /* may not exist */ }
      }
    }
  }

  // ── Send Message (compose modal) ──
  function openComposeMessage(app, campaign, prefilledText = '') {
    setComposeRecipient({
      readerId: app.readerId || app.id,
      readerName: app.readerName || 'Reader',
      readerEmail: app.readerEmail || '',
    });
    setComposeBookTitle(campaign.bookTitle || campaign.name || '');
    setComposeBookId(campaign.bookId || '');
    setComposeText(prefilledText);
    setShowComposeModal(true);
  }

  function openRequestReview(app, campaign) {
    const readerFirstName = (app.readerName || 'there').split(' ')[0];
    const bookTitle = campaign.bookTitle || campaign.name || 'your ARC';
    const reviewText = `Hi ${readerFirstName},\n\nWe hope you're enjoying ${bookTitle}! We'd love to hear your thoughts — if you've finished reading, please do leave a review. The book launches soon and reviews make a huge difference.\n\nThank you so much!`;
    openComposeMessage(app, campaign, reviewText);
  }

  async function handleSendMessage() {
    if (!composeText.trim() || !composeRecipient || !authorId) return;
    setIsSendingMessage(true);
    try {
      const selectedBook = books.find(b => b.id === composeBookId);
      const threadData = {
        authorId,
        readerId: composeRecipient.readerId,
        readerName: composeRecipient.readerName,
        readerEmail: composeRecipient.readerEmail,
        bookId: composeBookId || '',
        bookTitle: selectedBook?.title || composeBookTitle || '',
        lastMessage: composeText.trim(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        unreadByAuthor: false,
        unreadByReader: true,
        messageType: 'book_feedback',
      };
      const threadRef = await addDoc(collection(db, 'authors', authorId, 'messages'), threadData);
      const threadId = threadRef.id;

      // Add message to subcollection
      await addDoc(collection(db, 'authors', authorId, 'messages', threadId, 'messages'), {
        content: composeText.trim(),
        sender: 'author',
        createdAt: serverTimestamp(),
      });

      // Dual-write reader index
      await setDoc(doc(db, 'readers', composeRecipient.readerId, 'messageThreads', threadId), {
        authorId,
        authorName: 'Author',
        bookId: composeBookId || '',
        bookTitle: selectedBook?.title || composeBookTitle || '',
        threadType: 'author',
        lastMessage: composeText.trim(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        unreadByReader: true,
      });

      setShowComposeModal(false);
      setComposeRecipient(null);
      setComposeText('');
      setComposeBookTitle('');
      setComposeBookId('');
    } catch (err) {
      console.error('Send message error:', err);
    } finally {
      setIsSendingMessage(false);
    }
  }

  // ── Remove from ARC ──
  function openRemoveConfirm(app, campaignId, campaign) {
    setRemoveTarget({ app, campaignId, campaign });
    setShowRemoveModal(true);
  }

  async function handleRemoveFromArc() {
    if (!removeTarget || !basePath) return;
    setIsRemoving(true);
    try {
      const { app, campaignId, campaign } = removeTarget;
      const appRef = doc(db, basePath, 'campaigns', campaignId, 'applications', app.id);

      // Only give spot back if they were previously approved
      const wasApproved = app.status === 'approved';

      await updateDoc(appRef, { status: 'removed', decidedAt: serverTimestamp() });

      if (wasApproved) {
        const campaignRef = doc(db, basePath, 'campaigns', campaignId);
        console.log(`[ARC] Removing ${app.readerName} — incrementing spotsRemaining on campaign ${campaignId}`);
        await updateDoc(campaignRef, {
          spotsRemaining: increment(1),
          approvedCount: increment(-1),
        });

        // Delete per-book reader record
        const campaignBookId = campaign?.bookId;
        const readerId = app.readerId || app.id;
        if (campaignBookId) {
          const docId = readerId || app.readerEmail;
          console.log(`[ARC] Deleting per-book reader ${docId} from book ${campaignBookId}`);
          try {
            await deleteDoc(doc(db, basePath, 'books', campaignBookId, 'readers', docId));
          } catch { /* may not exist */ }
        }

        // If campaign was closed and now has spots, revert to active
        const campaignSnap = await getDoc(campaignRef);
        if (campaignSnap.exists()) {
          const data = campaignSnap.data();
          if ((data.spotsRemaining ?? 0) > 0 && normaliseStatus(data.status) === 'closed') {
            console.log(`[ARC] Spots freed up — reverting campaign ${campaignId} to active`);
            await updateDoc(campaignRef, { status: 'active', updatedAt: serverTimestamp() });
          }
        }
      }

      setShowRemoveModal(false);
      setRemoveTarget(null);
    } catch (err) {
      console.error('Remove from ARC error:', err);
    } finally {
      setIsRemoving(false);
    }
  }

  // Progress stepper component
  function CampaignStepper({ campaign }) {
    const currentIndex = getStageIndex(campaign.status);

    return (
      <div className="flex items-center gap-1 py-2">
        {CAMPAIGN_STAGES.map((stage, i) => {
          const Icon = stage.icon;
          const isCurrent = i === currentIndex;
          const isPast = i < currentIndex;
          const isNext = i === currentIndex + 1;

          return (
            <React.Fragment key={stage.id}>
              {i > 0 && (
                <div className={`h-0.5 w-6 ${isPast || isCurrent ? 'bg-teal-400' : 'bg-slate-200'}`} />
              )}
              <button
                onClick={() => {
                  if (isNext || isCurrent) {
                    if (isNext) handleCampaignStageChange(campaign, stage.id);
                  }
                }}
                disabled={!isNext}
                title={isNext ? `Progress to ${stage.label}` : stage.desc}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  isCurrent
                    ? stage.colour + ' ring-2 ring-offset-1 ring-teal-400'
                    : isPast
                      ? 'bg-teal-50 text-teal-600'
                      : isNext
                        ? 'bg-slate-100 text-slate-500 hover:bg-slate-200 cursor-pointer border border-dashed border-slate-300'
                        : 'bg-slate-50 text-slate-300'
                }`}
              >
                <Icon className="h-3 w-3" />
                {stage.label}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">ARC Campaigns</h1>
          <p className="text-sm text-slate-500">Manage your ARC campaigns and reader applications</p>
        </div>
        <Button
          className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
          onClick={() => { resetForm(); setShowForm(true); }}
          disabled={books.length === 0}
        >
          <Plus className="h-4 w-4" /> New Campaign
        </Button>
      </div>

      {books.length === 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          You need to add a book before creating a campaign.
        </div>
      )}

      {/* Campaign lifecycle guide */}
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
        <span className="font-semibold text-slate-700">Campaign stages:</span>
        {CAMPAIGN_STAGES.map((stage, i) => (
          <React.Fragment key={stage.id}>
            {i > 0 && <ArrowRight className="h-3 w-3 text-slate-300" />}
            <Badge className={`border-0 text-[10px] ${stage.colour}`}>{stage.label}</Badge>
          </React.Fragment>
        ))}
      </div>

      {/* Campaign List */}
      {campaigns.length === 0 ? (
        <Card className="border-dashed border-slate-200">
          <CardContent className="p-8 text-center">
            <Megaphone className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">No campaigns yet. Create one to start finding ARC readers.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {campaigns.map(campaign => {
            const book = books.find(b => b.id === campaign.bookId);
            const isExpanded = expandedCampaign === campaign.id;
            const stageConfig = getStageConfig(campaign.status);
            const currentIndex = getStageIndex(campaign.status);
            const nextStage = currentIndex < CAMPAIGN_STAGES.length - 1 ? CAMPAIGN_STAGES[currentIndex + 1] : null;
            const hasPrevStage = currentIndex > 0;

            return (
              <Card key={campaign.id} className="border-slate-200">
                <CardContent className="p-0">
                  {/* Campaign header */}
                  <div className="p-4">
                    <div className="flex items-center gap-4">
                      {book?.coverUrl ? (
                        <img src={book.coverUrl} alt="" className="h-16 w-11 rounded object-cover" />
                      ) : (
                        <div className="flex h-16 w-11 items-center justify-center rounded bg-slate-100">
                          <BookOpen className="h-4 w-4 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="truncate font-medium text-slate-900">{campaign.name}</h3>
                          <Badge className={`border-0 text-[10px] ${stageConfig.colour}`}>
                            {stageConfig.label}
                          </Badge>
                        </div>
                        <div className="mt-1 flex items-center gap-4 text-xs text-slate-500">
                          <span>{campaign.spotsRemaining ?? campaign.spotsAvailable} / {campaign.spotsAvailable} spots remaining</span>
                          <span>{campaign.arcFormat}</span>
                          {campaign.openDate && <span>Opens {campaign.openDate}</span>}
                          {campaign.closeDate && <span>Closes {campaign.closeDate}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {hasPrevStage && (
                          <Button size="sm" variant="outline" className="gap-1 text-xs text-slate-500 hover:bg-slate-50"
                            onClick={() => openStageBackConfirm(campaign)}>
                            <ArrowLeft className="h-3 w-3" /> Back
                          </Button>
                        )}
                        {nextStage && (
                          <Button size="sm" variant="outline" className="gap-1 text-xs"
                            onClick={() => handleCampaignStageChange(campaign, nextStage.id)}>
                            <ArrowRight className="h-3 w-3" /> {nextStage.label}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-xs"
                          onClick={() => openEdit(campaign)}>
                          Edit
                        </Button>
                        <button
                          onClick={() => setExpandedCampaign(isExpanded ? null : campaign.id)}
                          className="rounded p-1 hover:bg-slate-100"
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                        </button>
                      </div>
                    </div>

                    {/* Progress stepper */}
                    <CampaignStepper campaign={campaign} />
                  </div>

                  {/* Expanded: Applications */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50/50 p-4">
                      <div className="mb-3 flex items-center gap-4 text-xs text-slate-500">
                        <span>{applications.length} total applications</span>
                        <span>{applications.filter(a => a.status === 'pending').length} pending</span>
                        <span>{applications.filter(a => a.status === 'approved').length} approved</span>
                        <span>{applications.filter(a => a.status === 'interested').length} interested</span>
                      </div>
                      {applications.length === 0 ? (
                        <p className="text-sm text-slate-500">No applications yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {applications.map(app => (
                            <div key={app.id} className="flex items-center justify-between rounded-lg bg-white p-3 border border-slate-100">
                              <div>
                                <p className="text-sm font-medium text-slate-900">{app.readerName || 'Unknown Reader'}</p>
                                <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500">
                                  {app.readerScore > 0 && <span>Score: {app.readerScore}</span>}
                                  {app.arcsCompleted != null && <span>{app.arcsCompleted} ARCs done</span>}
                                  <Badge className={`border-0 text-[10px] ${
                                    app.status === 'approved' ? 'bg-green-100 text-green-700' :
                                    app.status === 'declined' ? 'bg-red-100 text-red-700' :
                                    app.status === 'removed' ? 'bg-red-100 text-red-700' :
                                    app.status === 'waitlisted' ? 'bg-yellow-100 text-yellow-700' :
                                    app.status === 'interested' ? 'bg-blue-100 text-blue-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}>{app.status || 'pending'}</Badge>
                                </div>
                                {app.message && (
                                  <p className="mt-1 text-xs text-slate-500 italic">"{app.message}"</p>
                                )}
                              </div>
                              {(!app.status || app.status === 'pending' || app.status === 'interested') && (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" className="gap-1 text-xs text-green-600 hover:bg-green-50"
                                    onClick={() => handleApplicationDecision(campaign.id, app.id, 'approved')}>
                                    <CheckCircle className="h-3 w-3" /> Approve
                                  </Button>
                                  <Button size="sm" variant="outline" className="gap-1 text-xs text-yellow-600 hover:bg-yellow-50"
                                    onClick={() => handleApplicationDecision(campaign.id, app.id, 'waitlisted')}>
                                    <Clock className="h-3 w-3" /> Waitlist
                                  </Button>
                                  <Button size="sm" variant="outline" className="gap-1 text-xs text-red-600 hover:bg-red-50"
                                    onClick={() => handleApplicationDecision(campaign.id, app.id, 'declined')}>
                                    <XCircle className="h-3 w-3" /> Decline
                                  </Button>
                                </div>
                              )}
                              {app.status === 'approved' && (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" className="gap-1 text-xs text-teal-600 hover:bg-teal-50"
                                    onClick={() => openComposeMessage(app, campaign)}>
                                    <MessageCircle className="h-3 w-3" /> Send Message
                                  </Button>
                                  <Button size="sm" variant="outline" className="gap-1 text-xs text-teal-600 hover:bg-teal-50"
                                    onClick={() => openRequestReview(app, campaign)}>
                                    <Star className="h-3 w-3" /> Request Review
                                  </Button>
                                  <Button size="sm" variant="outline" className="gap-1 text-xs text-red-600 hover:bg-red-50"
                                    onClick={() => openRemoveConfirm(app, campaign.id, campaign)}>
                                    <UserMinus className="h-3 w-3" /> Remove
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Email send log for this campaign's book */}
                      {campaignEmails.length > 0 && (
                        <div className="mt-4 border-t border-slate-200 pt-4">
                          <p className="mb-2 text-xs font-semibold text-slate-500">ARC Emails Sent ({campaignEmails.length})</p>
                          <div className="space-y-1.5">
                            {campaignEmails.slice(0, 10).map(em => {
                              const ts = em.sentAt?.toDate?.() || em.createdAt?.toDate?.();
                              return (
                                <div key={em.id} className="flex items-center justify-between rounded-lg bg-white border border-slate-100 px-3 py-2 text-xs">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className={`inline-block h-2 w-2 rounded-full ${em.status === 'sent' ? 'bg-green-500' : em.status === 'failed' ? 'bg-red-500' : 'bg-yellow-400'}`} />
                                    <span className="font-medium text-slate-900 truncate">{em.recipientName || em.recipientEmail}</span>
                                  </div>
                                  <div className="flex items-center gap-3 shrink-0 text-slate-400">
                                    {em.hasAttachment && <span className="text-teal-500">📎</span>}
                                    <span>{ts ? ts.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}</span>
                                    <Badge className={`border-0 text-[10px] ${em.status === 'sent' ? 'bg-green-100 text-green-700' : em.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                      {em.status}
                                    </Badge>
                                  </div>
                                </div>
                              );
                            })}
                            {campaignEmails.length > 10 && (
                              <p className="text-xs text-slate-400 text-center">+ {campaignEmails.length - 10} more</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Campaign Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) { resetForm(); setShowForm(false); } }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'New ARC Campaign'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-600">{error}</div>
            )}
            <div>
              <Label>Book *</Label>
              <select
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                value={bookId}
                onChange={e => {
                  setBookId(e.target.value);
                  if (!name) {
                    const b = books.find(b => b.id === e.target.value);
                    if (b) setName(b.title);
                  }
                }}
              >
                <option value="">Select a book...</option>
                {books.filter(b => b.status !== 'archived').map(b => (
                  <option key={b.id} value={b.id}>{b.title}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Campaign Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Defaults to book title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>ARC Spots</Label>
                <Input type="number" value={spotsAvailable} onChange={e => setSpotsAvailable(e.target.value)} />
              </div>
              <div>
                <Label>Format</Label>
                <select
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={arcFormat}
                  onChange={e => setArcFormat(e.target.value)}
                >
                  <option value="ebook">eBook (Kindle/ePub)</option>
                  <option value="physical">Physical Copy</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Applications Open</Label>
                <Input type="date" value={openDate} onChange={e => setOpenDate(e.target.value)} />
              </div>
              <div>
                <Label>Applications Close</Label>
                <Input type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Requirements for Applicants</Label>
              <Textarea value={requirements} onChange={e => setRequirements(e.target.value)}
                placeholder="e.g. Must post review on Amazon and Goodreads within 4 weeks" rows={2} />
            </div>
            <div>
              <Label>Preferred Reviewer Profiles</Label>
              <Textarea value={preferredProfiles} onChange={e => setPreferredProfiles(e.target.value)}
                placeholder="e.g. Readers who enjoy romantasy, active on BookTok" rows={2} />
            </div>
            <div>
              <Label>Pitch to Readers</Label>
              <Textarea value={pitch} onChange={e => setPitch(e.target.value)}
                placeholder="Why should readers want to read this book?" rows={3} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { resetForm(); setShowForm(false); }}>Cancel</Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={handleSave} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : editingCampaign ? 'Update Campaign' : 'Create Campaign'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Compose Message Dialog */}
      <Dialog open={showComposeModal} onOpenChange={(open) => { if (!open) { setShowComposeModal(false); setComposeRecipient(null); setComposeText(''); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {composeRecipient && (
              <div className="flex items-center gap-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-100 text-sm font-medium text-teal-700">
                  {(composeRecipient.readerName || 'R').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{composeRecipient.readerName}</p>
                  {composeBookTitle && (
                    <p className="text-xs text-teal-600 flex items-center gap-1">
                      <BookOpen className="h-3 w-3" /> {composeBookTitle}
                    </p>
                  )}
                </div>
              </div>
            )}
            <Textarea
              value={composeText}
              onChange={e => setComposeText(e.target.value)}
              placeholder="Write your message..."
              rows={5}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowComposeModal(false); setComposeRecipient(null); setComposeText(''); }}>
                Cancel
              </Button>
              <Button
                className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
                onClick={handleSendMessage}
                disabled={isSendingMessage || !composeText.trim()}
              >
                <Send className="h-4 w-4" /> {isSendingMessage ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove from ARC Confirmation Dialog */}
      <Dialog open={showRemoveModal} onOpenChange={(open) => { if (!open) { setShowRemoveModal(false); setRemoveTarget(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove from ARC</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Remove <span className="font-medium text-slate-900">{removeTarget?.app?.readerName}</span> from this ARC campaign? They will lose access to the ARC copy.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowRemoveModal(false); setRemoveTarget(null); }}
                disabled={isRemoving}>
                Cancel
              </Button>
              <Button
                className="gap-1.5 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleRemoveFromArc}
                disabled={isRemoving}
              >
                <UserMinus className="h-4 w-4" /> {isRemoving ? 'Removing...' : 'Remove Reader'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stage Back Confirmation Dialog */}
      <Dialog open={showStageBackModal} onOpenChange={(open) => { if (!open) { setShowStageBackModal(false); setStageBackTarget(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Move Campaign Back</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Move this campaign back to <span className="font-medium text-slate-900">{stageBackTarget?.previousStage?.label}</span>? This will update what readers see in the portal.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowStageBackModal(false); setStageBackTarget(null); }}>
                Cancel
              </Button>
              <Button
                className="gap-1.5 bg-slate-700 hover:bg-slate-800 text-white"
                onClick={handleCampaignStageBack}
              >
                <ArrowLeft className="h-4 w-4" /> Move to {stageBackTarget?.previousStage?.label}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
